import type { Organization } from "@/models/organization"
import type { User } from "@/models/user"
import { httpClient } from "@/utils/api"
import type { SuccessData } from "@/utils/api"
import type { UseMutationOptions } from "@tanstack/react-query"
import type { AxiosError, AxiosResponse } from "axios"

import type { UserMutationError } from "./use-user-mutation"
import { useUserMutation } from "./use-user-mutation"

export interface UserUpdate {
  email: User["email"]
  first_name: User["firstName"]
  last_name: User["lastName"]
  organization_ids?: Organization["id"][]
  role: User["role"]
}

export type UpdateUserError = UserMutationError

const updateUserApi = async ({
  userId,
  user,
}: {
  userId: string
  user: UserUpdate
}): Promise<AxiosResponse<SuccessData>> => {
  try {
    const response = await httpClient.put<SuccessData>(
      `/api/v1/users/${userId}`,
      {
        user,
      }
    )

    return response
  } catch {
    console.error("Error updating user")
    throw new Error("Failed to update user")
  }
}

export const useUpdateUser = (
  options?: UseMutationOptions<
    AxiosResponse<SuccessData>,
    AxiosError<UpdateUserError>,
    { userId: string; user: UserUpdate }
  >
) =>
  useUserMutation({
    mutationFn: updateUserApi,
    resultKey: "updateUser",
    fallbackErrorMessage: "editUser.updateUserError",
    options,
  })
